import { useState } from "react";
import { useNotify, useRecordContext, useTranslate } from "ra-core";
import { Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";

// Invite-only access without email: the admin copies a one-time link and sends it
// over WhatsApp/WeChat. The link logs the person in and opens the set-password page.
export const LoginLinkButton = (_props: { label?: string | boolean }) => {
  const sale = useRecordContext<Sale>();
  const notify = useNotify();
  const translate = useTranslate();
  const [busy, setBusy] = useState(false);
  if (!sale) return null;

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: { link: string };
      }>("users", {
        method: "POST",
        body: { action: "login_link", sales_id: sale.id },
      });
      const link = data?.data?.link;
      if (error || !link) throw new Error(translate("egi.login_link.failed"));
      try {
        await navigator.clipboard.writeText(link);
        notify("egi.login_link.copied", { messageArgs: { email: sale.email } });
      } catch {
        window.prompt(
          translate("egi.login_link.prompt", { email: sale.email }),
          link,
        );
      }
    } catch (error: unknown) {
      notify(error instanceof Error ? error.message : "Unexpected error", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={copy} disabled={busy}>
      <Link2 className="size-4" />
      {translate(busy ? "egi.login_link.generating" : "egi.login_link.copy")}
    </Button>
  );
};
